import { useAppContext } from "context/appContext";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { MemberDto, RecipientMode } from "types";
import SMSRecipientNoVerified from "./SMSRecipientNoVerified";
import { getMemberName } from "utils/organisationFunction";
import SelectRecipientModal from "components/modals/SelectRecipientModal";
import { AlertConfig } from "types/Gateways";
import {
    NOT_FOUND_PHONE_NUMBER_MESSAGE,
    SMS_RECIPIENT_MESSAGE,
} from "constant";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { CloseOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { APP_CONFIG } from "config/appConfig";

type AlertNoticeConfigProps = {
    config: AlertConfig;
    disabled?: boolean;
    onChange?: (config: AlertConfig) => void;
    initialRecipients: MemberDto[];
    showOnlyNotification?: boolean; // If true, only show Push Notification option
};

const Recipient = ({
    givenName,
    familyName,
    recipient,
}: {
    givenName: string;
    familyName: string;
    recipient: string;
}) => (
    <div className="added-item position-relative">
        <p className="mb-0 mr-4 added-item-recipient">{`${givenName} ${familyName}`}</p>{" "}
        <h6 className="ginfo">{recipient}</h6>
    </div>
);

const AlertNoticeConfig = ({
    config: defaultConfig,
    initialRecipients = [],
    onChange,
    disabled,
    showOnlyNotification = false,
}: AlertNoticeConfigProps) => {
    const [config, setConfig] = useState<AlertConfig>(getDefaultAlertConfig());
    const [recipientMode, setRecipientMode] = useState<RecipientMode>("Email");
    const [selectRecipientShow, setSelectRecipientShow] = useState(false);
    const {
        storeData: {
            organization: {
                currentOrgInfo: { isDefault, owner },
            },
            user: {
                userData: {
                    phoneNumber,
                    phoneNumberVerified,
                    givenName,
                    familyName,
                },
            },
        },
    } = useAppContext();
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    // Disable Send SMS/Email for Cloud + Basic Plan (or) On-prem
    const shouldDisableSmsEmail =
        (IS_BASIC_PLAN && !APP_CONFIG.APP_IS_ONPREM) || (APP_CONFIG.APP_IS_ONPREM);


    useEffect(() => {
        setConfig(defaultConfig);
    }, [defaultConfig]);

    const updateConfig = (config: AlertConfig) => {
        setConfig(config);
        onChange?.(config);
    };

    const toggleAlert = (mode: RecipientMode) => {
        const toggleFunction = () => {
            const _config = { ...config };
            _config[mode].isEnabled = !config[mode].isEnabled;
            if (isDefault) {
                _config[mode].recipients = [...initialRecipients];
            }
            updateConfig(_config);
        };

        // Check wallet subscription when enabling alert
        if (config[mode].isEnabled) {
            toggleFunction();
        } else {
            // Subscribed user with low balance, able to enable alert. but alert wont be send. IOTP-10941
            walletModalTrigger(toggleFunction, true);
        }
    };

    const setRecipientList = (mode: RecipientMode, recipients: MemberDto[]) => {
        const _config = { ...config };
        _config[mode].recipients = recipients;
        updateConfig(_config);
    };

    const removeRecipientFromList = (
        mode: RecipientMode,
        recipient: string
    ) => {
        const _config = { ...config };
        _config[mode].recipients = config[mode].recipients.filter(
            (r) => r.username !== recipient
        );
        updateConfig(_config);
    };

    const renderRecipient = ({
        recipientList,
        recipientMode,
    }: {
        recipientList: MemberDto[];
        recipientMode: RecipientMode;
    }) => {
        const filtered = recipientList.filter(Boolean);

        if (isDefault) {
            if (["Email", "Notification"].includes(recipientMode)) {
                return (
                    <Recipient
                        givenName={givenName}
                        familyName={familyName}
                        recipient={owner}
                    />
                );
            } else if (recipientMode === "SMS") {
                if (phoneNumberVerified) {
                    return (
                        <Recipient
                            givenName={givenName}
                            familyName={familyName}
                            recipient={phoneNumber ?? ""}
                        />
                    );
                }

                return <SMSRecipientNoVerified />;
            }
        }

        const returnUserinfo = (username: string) => {
            if (isDefault) {
                return recipientMode === "SMS" ? phoneNumber : username;
            } else {
                return username;
            }
        };

        if (filtered) {
            const lst = filtered.map((item) => {
                const { username, uuid, phoneNumberVerified } = item;
                return (
                    <div className="added-item position-relative" key={uuid}>
                        <p className="mb-0 added-item-recipient">
                            {getMemberName(item)}
                        </p>{" "}
                        <h6 className="ginfo">{returnUserinfo(username)}</h6>
                        <CloseOutlined
                            className="recipient-close-button"
                            style={{ color: "#f2495e", cursor: "pointer" }}
                            onClick={() =>
                                removeRecipientFromList(recipientMode, username)
                            }
                            onKeyDown={() =>
                                removeRecipientFromList(recipientMode, username)
                            }
                        />
                        {recipientMode === "SMS" &&
                            phoneNumberVerified === false && (
                                <span className="d-flex align-items-center ml-0">
                                    <i className="material-icons">
                                        error_outline
                                    </i>
                                    <span className="text-size-12">
                                        {NOT_FOUND_PHONE_NUMBER_MESSAGE}
                                    </span>
                                </span>
                            )}
                    </div>
                );
            });
            return lst;
        }
    };

    const renderSelectRecipient = (recipientMode: RecipientMode) => {
        return (
            <div key={recipientMode}>
                <Row>
                    <Col sm={4}>
                        <p>
                            {`${recipientMode === "Notification"
                                ? `Push Notification`
                                : recipientMode
                                } Recipients`}
                        </p>
                    </Col>
                </Row>
                {!isDefault && (
                    <Row className="mb-3">
                        <Col sm={4}>
                            <InputGroup>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setRecipientMode(recipientMode);
                                        setSelectRecipientShow(true);
                                    }}
                                >
                                    Add +
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>
                )}
            </div>
        );
    };

    return (
        <>
            <Form.Label className="mt-3 mb-3">Send Alert</Form.Label>

            {!IS_BASIC_PLAN && (
                <WalletAlertComponent showWarningWhen="LowBalance" />
            )}
            {!APP_CONFIG.APP_IS_ONPREM && (
                <>
                    {!showOnlyNotification && (
                        <div className="recipients email">
                            <div
                                className={`recipient-option ${shouldDisableSmsEmail ? "disabled" : ""
                                    }`}
                            >
                                <Form.Switch
                                    name="enable-email"
                                    id="enable-email"
                                    checked={config.Email.isEnabled}
                                    custom
                                    label={
                                        <span className="ml-4">Send Email</span>
                                    }
                                    onChange={() => toggleAlert("Email")}
                                    disabled={disabled || shouldDisableSmsEmail}
                                />
                            </div>
                            <div
                                className={
                                    config.Email.isEnabled
                                        ? "recipient-box show"
                                        : "recipient-box"
                                }
                            >
                                <Row>
                                    <Col sm={12}>
                                        {renderSelectRecipient("Email")}
                                    </Col>
                                </Row>
                                <Row>
                                    {renderRecipient({
                                        recipientMode: "Email",
                                        recipientList: config.Email.recipients,
                                    })}
                                </Row>
                            </div>
                        </div>
                    )}
                    {!showOnlyNotification && (
                        <div className="recipients sms mt-3">
                            <Box
                                className={`recipient-option ${shouldDisableSmsEmail ? "disabled" : ""
                                    }`}
                                onClick={() => {
                                    if (disabled || shouldDisableSmsEmail) return;
                                    toggleAlert("SMS");
                                }}
                                onKeyDown={() => {
                                    if (disabled || shouldDisableSmsEmail) return;
                                    toggleAlert("SMS");
                                }}
                            >
                                <Form.Switch
                                    name="enable-sms"
                                    id="enable-sms"
                                    checked={config.SMS.isEnabled}
                                    custom
                                    label={<span className="ml-4">Send SMS</span>}
                                    onChange={() => {
                                        toggleAlert("SMS");
                                    }}
                                    disabled={disabled || shouldDisableSmsEmail}
                                />
                            </Box>
                            <div
                                className={
                                    config.SMS.isEnabled
                                        ? "recipient-box show"
                                        : "recipient-box"
                                }
                            >
                                <Row>
                                    <Col sm={12}>
                                        {renderSelectRecipient("SMS")}
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        {renderRecipient({
                                            recipientMode: "SMS",
                                            recipientList: config.SMS.recipients,
                                        })}
                                    </Col>
                                </Row>
                                {!isDefault && (
                                    <Row className="text-size-12 text-primary-blue-4">
                                        <Col className="mt-12 recipientFooter">
                                            {SMS_RECIPIENT_MESSAGE}
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="recipients notification mt-3">
                <div className="recipient-option">
                    <Form.Switch
                        name="enable-notification"
                        id="enable-notification"
                        checked={config.Notification.isEnabled}
                        custom
                        type="switch"
                        label={<span className="ml-4">Send Notification</span>}
                        onChange={() => toggleAlert("Notification")}
                        disabled={disabled}
                    />
                </div>
                <div
                    className={
                        config.Notification.isEnabled
                            ? "recipient-box show"
                            : "recipient-box"
                    }
                >
                    <Row>
                        <Col sm={12}>
                            {renderSelectRecipient("Notification")}
                        </Col>
                    </Row>
                    <Row>
                        {renderRecipient({
                            recipientMode: "Notification",
                            recipientList: config.Notification.recipients,
                        })}
                    </Row>
                </div>
            </div>

            <SelectRecipientModal
                {...{
                    show: selectRecipientShow,
                    recipientMode,
                    emailList: config.Email.recipients,
                    setEmailList: (recipients: MemberDto[]) =>
                        setRecipientList("Email", recipients),
                    notificationList: config.Notification.recipients,
                    setNotificationList: (recipients: MemberDto[]) =>
                        setRecipientList("Notification", recipients),
                    smsList: config.SMS.recipients,
                    setSMSList: (recipients: MemberDto[]) =>
                        setRecipientList("SMS", recipients),
                    onClose: () => setSelectRecipientShow(false),
                    initialMemberList: initialRecipients,
                }}
            />
        </>
    );
};

export const getDefaultAlertConfig = () =>
({
    Email: {
        isEnabled: false,
        recipientMode: "Email",
        recipients: [],
    },
    SMS: {
        isEnabled: false,
        recipientMode: "SMS",
        recipients: [],
    },
    Notification: {
        isEnabled: false,
        recipientMode: "Notification",
        recipients: [],
    },
} as AlertConfig);

export default AlertNoticeConfig;
